/* Romina product UI — vanilla ES2020, no jQuery.
   Responsibilities:
     1. Popup modal open/close + swatch pick → set hidden `[data-single-option-selector]`
        and delegate to Pipeline's Alpine `changeVariant` on the product form.
     2. Metafield add-on picks (handles / feet / accent-finish-product / custom-option / fabric-product):
        set hidden input, recompute price, update the row's displayed swatch + label.
     3. Delivery estimate — quick-ship (10–20 biz days) vs standard (50–60 biz days),
        re-renders on variant change + history push/replace.
     4. multiAtc cart intercept — if product.metafields.atc.multi is set, split the ATC
        submit into base + handles(N) + feet(N) + accent/custom/fabric line items.
     5. Tabs toggle (accordion) on the bottom product tabs.
*/
(function () {
  'use strict';

  // ---- formatting -----------------------------------------------------------
  const fmtMoney = (cents) => {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt =
          (window.theme && window.theme.moneyFormat) ||
          (window.Shopify && window.Shopify.money_format) ||
          '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
    } catch (_) { /* noop */ }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  };

  // ---- popup engine ---------------------------------------------------------
  // Pipeline's quickview/drawer uses CSS transform to slide in. A descendant
  // with position: fixed is then positioned relative to the transformed
  // ancestor, not the viewport — so the popup would render off-screen or
  // clipped. We sidestep this by re-parenting the popup to <body> on open and
  // sending it back to its original parent on close.
  const openPopup = (popup) => {
    if (!popup) return;
    if (popup.parentNode !== document.body) {
      // Stash original location so we can restore on close
      popup.__rominaParent = popup.parentNode;
      popup.__rominaNext = popup.nextSibling;
      document.body.appendChild(popup);
    }
    popup.setAttribute('data-open', 'true');
    document.body.classList.add('romina-popup-open');
    const closeBtn = popup.querySelector('.romina-popup__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  };

  const closePopup = (popup) => {
    if (!popup) return;
    popup.removeAttribute('data-open');
    if (!document.querySelector('.romina-popup[data-open="true"]')) {
      document.body.classList.remove('romina-popup-open');
    }
    const trigger = document.querySelector(
      '[data-romina-popup-trigger="' + popup.id + '"]'
    );
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    // Restore the popup to its original DOM location so morph/section reloads
    // can find it (and so the data-* trigger lookup keeps working).
    if (popup.__rominaParent) {
      try {
        popup.__rominaParent.insertBefore(popup, popup.__rominaNext || null);
      } catch (_) { /* parent may have been removed; leave popup in body */ }
      popup.__rominaParent = null;
      popup.__rominaNext = null;
    }
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-romina-popup-trigger]');
    if (trigger) {
      e.preventDefault();
      const id = trigger.getAttribute('data-romina-popup-trigger');
      const popup = document.getElementById(id);
      if (popup) {
        trigger.setAttribute('aria-expanded', 'true');
        openPopup(popup);
      }
      return;
    }
    if (e.target.closest('[data-romina-popup-close]')) {
      e.preventDefault();
      const popup = e.target.closest('.romina-popup');
      closePopup(popup);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const open = document.querySelector('.romina-popup[data-open="true"]');
      if (open) closePopup(open);
    }
  });

  // ---- swatch pick — variant options ---------------------------------------
  // A variant popup item has:
  //   data-romina-pick
  //   data-value           — option value text
  //   data-image           — swatch image URL (optional)
  //   data-variant-id      — variant ID (if this value uniquely maps to a variant)
  //   data-variant-url     — variant URL for Pipeline's changeVariant
  //   data-media-id        — featured media id for Pipeline's changeVariant
  //
  // An add-on popup item has:
  //   data-romina-pick
  //   data-addon           — addon key (handles | feet | accent | custom | fabric)
  //   data-id              — variant id of the addon product's variant
  //   data-price           — price in cents
  //   data-value / data-image — display
  const updateRowDisplay = (row, value, imageUrl) => {
    const textEl = row.querySelector('[data-romina-selected-text]');
    if (textEl) textEl.textContent = value;
    const imgEl = row.querySelector('[data-romina-selected-img]');
    if (imgEl) {
      if (imageUrl) {
        imgEl.setAttribute('src', imageUrl);
        imgEl.hidden = false;
        imgEl.parentElement.classList.remove('romina-row__swatch--empty');
      } else {
        imgEl.removeAttribute('src');
        imgEl.hidden = true;
        imgEl.parentElement.classList.add('romina-row__swatch--empty');
      }
    }
  };

  // Move gallery to a specific media id. Hits Pipeline's flickity handler
  // directly so it works even when product-form re-render is morph-skipped
  // and Alpine `changeVariant` does not propagate mediaId.
  const selectGalleryMedia = (mediaId) => {
    if (!mediaId) return;
    const slideshow =
      document.querySelector('[data-product-slideshow]') ||
      document.querySelector('[data-product-slideshow-desktop]') ||
      document.querySelector('[data-product-slideshow-mobile]');
    if (!slideshow) return;
    const dispatch = () => {
      slideshow.dispatchEvent(new CustomEvent('theme:image:change', {
        detail: { id: String(mediaId) },
      }));
    };
    if (slideshow.classList.contains('flickity-enabled')) {
      dispatch();
    } else {
      // Wait for Flickity to attach, then dispatch
      const obs = new MutationObserver(() => {
        if (slideshow.classList.contains('flickity-enabled')) {
          obs.disconnect();
          dispatch();
        }
      });
      obs.observe(slideshow, { attributes: true, attributeFilter: ['class'] });
      // Safety timeout
      setTimeout(() => { obs.disconnect(); dispatch(); }, 1500);
    }
  };

  const callChangeVariant = (formEl, variantUrl, variantId, mediaId) => {
    if (!formEl) return;
    // Always nudge the gallery first — independent of Alpine path.
    selectGalleryMedia(mediaId);
    // Pipeline exposes `changeVariant` as an Alpine $data method on the
    // <product-form>. We call it via Alpine's global if available, else
    // fall back to dispatching a submit of the form.
    try {
      const alpineData = window.Alpine && window.Alpine.$data
        ? window.Alpine.$data(formEl)
        : null;
      if (alpineData && typeof alpineData.changeVariant === 'function') {
        alpineData.changeVariant(variantUrl, variantId, mediaId);
        // Re-dispatch after navigation completes — section re-render may swap slideshow
        setTimeout(() => selectGalleryMedia(mediaId), 50);
        setTimeout(() => selectGalleryMedia(mediaId), 350);
        return;
      }
    } catch (_) { /* noop */ }
    // Fallback — navigate the URL so server renders with new variant.
    if (variantUrl) {
      const base = window.location.origin;
      const url = variantUrl.startsWith('http') ? variantUrl : base + variantUrl;
      window.location.href = url;
    }
  };

  document.addEventListener('click', (e) => {
    const pick = e.target.closest('[data-romina-pick]');
    if (!pick) return;
    e.preventDefault();

    const popup = pick.closest('.romina-popup');
    const row = document.querySelector(
      '[data-romina-popup-trigger="' + (popup && popup.id) + '"]'
    )?.closest('.romina-row');

    const value = pick.getAttribute('data-value') || '';
    const image = pick.getAttribute('data-image') || '';

    // Mark active in the popup grid
    if (popup) {
      popup.querySelectorAll('[data-romina-pick]').forEach((el) => {
        el.classList.remove('is-active');
        el.setAttribute('aria-checked', 'false');
      });
      pick.classList.add('is-active');
      pick.setAttribute('aria-checked', 'true');
    }

    // Update the row chip display
    if (row) updateRowDisplay(row, value, image);

    const isAddon = pick.hasAttribute('data-romina-addon-pick');
    if (isAddon) {
      // Add-on pick: set hidden input, update price accumulator
      const addonInputId = pick.getAttribute('data-addon-input-id');
      const addonInput = addonInputId
        ? document.getElementById(addonInputId)
        : row && row.querySelector('[data-romina-addon-id]');
      if (addonInput) {
        addonInput.value = pick.getAttribute('data-variant-id') || '';
        addonInput.setAttribute(
          'data-price',
          pick.getAttribute('data-price') || '0'
        );
      }
      recalcPrice(pick.closest('[data-product-form-wrapper]'));
    } else {
      // Variant option pick: set the hidden option input and resolve combined variant
      const optionInputId = pick.getAttribute('data-option-input-id');
      const input = optionInputId ? document.getElementById(optionInputId) : null;
      if (input) {
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Resolve which variant matches the current combined option selection
      const formEl =
        pick.closest('[data-product-form-wrapper]') ||
        document.querySelector('[data-product-form-wrapper]');
      const resolved = resolveCombinedVariant(formEl);
      if (resolved) {
        // Always prefer the combined-resolution result. A popup pick's own
        // data-variant-id/data-media-id come from Liquid's `value.variant`,
        // which on a multi-option product resolves to the FIRST variant
        // carrying that option value — ignoring the other selected options.
        // e.g. picking "Bar Handle - Gold" yields Albero Puro + Gold even when
        // the Finish is Navy. resolved.* accounts for every option, so it wins;
        // the pick's data-* is only a fallback when resolution fails.
        const variantUrl =
          resolved.url || pick.getAttribute('data-variant-url');
        const variantId =
          resolved.id || pick.getAttribute('data-variant-id');
        const mediaId =
          resolved.mediaId || pick.getAttribute('data-media-id');
        callChangeVariant(
          document.querySelector('product-form[data-form-holder]') || formEl,
          variantUrl,
          variantId ? Number(variantId) : null,
          mediaId ? Number(mediaId) : null
        );
        // Update the price for the resolved variant (Handles/Knobs change the
        // variant price; Pipeline's re-render doesn't fire in this flow).
        updateVariantPrice(resolved);
        recalcPrice(formEl);
      }
      // Refresh the delivery ETA for the newly selected finish/variant. The
      // modal may not push ?variant= to the URL, so the history hook alone
      // won't catch this change.
      updateDelivery();
    }

    closePopup(popup);
  });

  // ---- combined variant resolution -----------------------------------------
  // Given the container, read every hidden `options[NAME]` input, look up the
  // matching variant in the embedded product JSON, and return { id, url, mediaId }.
  const resolveCombinedVariant = (container) => {
    if (!container) return null;
    const jsonEl =
      container.querySelector('[data-romina-product-json]') ||
      document.querySelector('[data-romina-product-json]');
    if (!jsonEl) return null;
    let data;
    try { data = JSON.parse(jsonEl.textContent); } catch (_) { return null; }
    if (!data || !Array.isArray(data.variants)) return null;

    const selectedValues = [];
    container.querySelectorAll('[data-romina-option-input]').forEach((el) => {
      const idx = Number(el.getAttribute('data-option-position')) - 1;
      selectedValues[idx] = el.value;
    });

    const matches = data.variants.filter((v) => {
      const opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
      for (let i = 0; i < selectedValues.length; i++) {
        if (selectedValues[i] && opts[i] !== selectedValues[i]) return false;
      }
      return true;
    });
    if (!matches.length) return null;
    // Prefer a matching variant that has a featured_media so the gallery
    // can snap to the variant's image. If none has one, fall back to the
    // first match.
    const match = matches.find((v) => v.featured_media && v.featured_media.id) || matches[0];

    // Resolve the gallery media. Combination variants (e.g. a finish + handle
    // pairing) often have no image assigned in admin, so match.featured_media
    // is null and the gallery would stay on the main image. The image is driven
    // by the visual option (Finish/colour), so fall back to a media-bearing
    // variant that shares one of the selected option values — prefer option 1
    // (Finish), then the rest.
    let mediaId = match.featured_media && match.featured_media.id;
    if (!mediaId) {
      for (let i = 0; i < selectedValues.length && !mediaId; i++) {
        if (!selectedValues[i]) continue;
        const alt = data.variants.find((v) =>
          v.featured_media && v.featured_media.id &&
          (v.options || [])[i] === selectedValues[i]
        );
        if (alt) mediaId = alt.featured_media.id;
      }
    }

    // Build a URL using the variant id (Shopify resolves ?variant= to the variant)
    const productUrl = data.url || ('/products/' + (data.handle || ''));
    const url = productUrl + '?variant=' + match.id;
    return {
      id: match.id,
      url: url,
      mediaId: mediaId,
      price: match.price,
      compareAt: match.compare_at_price
    };
  };

  // Update the displayed product price (+ compare-at) from a resolved variant.
  // The Romina picker drives variant changes manually, so Pipeline's own price
  // re-render doesn't always fire — price options like Handles/Knobs would
  // otherwise stay at the base variant's price. Drive it from the variant JSON.
  const updateVariantPrice = (resolved) => {
    if (!resolved || resolved.price == null) return;
    const calc = document.querySelector('[data-romina-wallpaper-calc]');
    if (calc && typeof calc.__rominaRecalc === 'function') {
      calc.__rominaRecalc(resolved);
      return;
    }
    document.querySelectorAll('[data-product-price]').forEach((el) => {
      el.textContent = fmtMoney(resolved.price);
    });
    // ATC button price label. Pipeline bakes `{{ price }}` as a Liquid literal
    // into the button's x-text (reactive to quantity only, never variantId),
    // and our manual variant flow doesn't trigger a server re-bake — so the
    // button would otherwise stay at the default variant price. Drive it here.
    const qtyEl =
      document.querySelector('[data-product-form-wrapper] [name="quantity"]') ||
      document.querySelector('form[data-product-form] [name="quantity"]') ||
      document.querySelector('[name="quantity"]');
    const qty = Math.max(1, Number(qtyEl && qtyEl.value) || 1);
    document.querySelectorAll('[data-button-price]').forEach((el) => {
      el.textContent = fmtMoney(resolved.price * qty);
    });
    const onSale = resolved.compareAt && resolved.compareAt > resolved.price;
    document.querySelectorAll('[data-compare-price]').forEach((el) => {
      if (onSale) {
        el.textContent = fmtMoney(resolved.compareAt);
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
    // Keep the add-on price accumulator's base in sync with the variant price.
    const picker = document.querySelector('[data-romina-picker]');
    if (picker) picker.setAttribute('data-base-price', resolved.price);
  };

  // ---- price accumulator ----------------------------------------------------
  // Add base variant price + each add-on (handles/feet multiply by qty).
  const recalcPrice = (container) => {
    if (!container) return;
    const picker = container.querySelector('[data-romina-picker]');
    const basePrice = Number(
      (picker && picker.getAttribute('data-base-price')) ||
        container.getAttribute('data-base-price') ||
        0
    );
    let total = basePrice;

    container.querySelectorAll('[data-romina-addon-id]').forEach((input) => {
      if (!input.value) return;
      const price = Number(input.getAttribute('data-price')) || 0;
      const qty = Number(input.getAttribute('data-qty')) || 1;
      total += price * qty;
    });

    const priceEl = container.querySelector('[data-romina-total-price]');
    const wrapEl = container.querySelector('[data-romina-total-wrapper]');
    if (priceEl) priceEl.textContent = fmtMoney(total);
    if (wrapEl) wrapEl.style.display = total !== basePrice ? '' : 'none';
  };

  // ---- delivery estimate ----------------------------------------------------
  const months = [
    'Jan','Feb','Mar','Apr','May','Jun',
    'Jul','Aug','Sep','Oct','Nov','Dec',
  ];

  const businessDatesRange = (minBiz, maxBiz) => {
    const now = Date.now();
    const day = 86400000;
    let d1, d2, bc = 0;
    for (let i = 1, n = 0; n < 365; i++, n++) {
      const c = new Date(now + i * day);
      const wd = c.getDay();
      if (wd !== 0 && wd !== 6) {
        bc++;
        if (bc === minBiz) d1 = c;
        if (bc === maxBiz) { d2 = c; break; }
      }
    }
    if (!d1 || !d2) return '';
    const sameMonth = d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
    if (sameMonth) {
      return months[d1.getMonth()] + ' ' + d1.getDate() + ' – ' + d2.getDate();
    }
    return months[d1.getMonth()] + ' ' + d1.getDate()
      + ' – ' + months[d2.getMonth()] + ' ' + d2.getDate();
  };

  const updateDelivery = () => {
    const host = document.querySelector('[data-romina-delivery]');
    if (!host) return;
    const mapEl = document.getElementById('romina-variant-status-map');
    let map = {};
    if (mapEl) {
      try { map = JSON.parse(mapEl.textContent); } catch (_) {}
    }
    // Current variant id. The Romina modal picker doesn't always push
    // ?variant= to the URL, so prefer its live combined-variant resolution;
    // then the URL param; then the initial variant.
    let vid = '';
    const formWrap =
      host.closest('[data-product-form-wrapper]') ||
      document.querySelector('[data-product-form-wrapper]');
    const resolvedV = resolveCombinedVariant(formWrap);
    if (resolvedV && resolvedV.id) vid = String(resolvedV.id);
    if (!vid) {
      const params = new URLSearchParams(window.location.search);
      vid =
        params.get('variant') ||
        host.getAttribute('data-initial-variant') ||
        '';
    }

    // Prefer the product-level Product Status metafield (rendered into
    // `data-product-status` / `data-is-quick` by romina-delivery-estimate.liquid).
    // Fall back to the legacy per-variant variant_status map.
    const productStatus = host.getAttribute('data-product-status') || '';
    const productIsQuickAttr = (host.getAttribute('data-is-quick') || '').toLowerCase() === 'true';

    const rawVariantStatus = (map && map[vid]) || '';
    const rawStatus = productStatus || rawVariantStatus;
    const status = rawStatus.toLowerCase();
    // Metafield / legacy variant-status signal (fallback).
    let isQuick =
      productIsQuickAttr ||
      status.indexOf('quick-ship') !== -1 ||
      status.indexOf('quick ship') !== -1;

    // Live CSV inventory override (single source of truth). When the selected
    // variant's SKU is found in the sheet it wins over the metafield. Unknown
    // SKU — or CSV not loaded yet — leaves the metafield fallback in place,
    // and updateDelivery re-runs on the 'romina:inventory:ready' event.
    let csvQuick = null;
    if (window.RominaInventory && typeof window.RominaInventory.getQuickShip === 'function') {
      let vsku = '';
      const skuMapEl = document.getElementById('romina-variant-sku-map');
      if (skuMapEl) {
        try { vsku = (JSON.parse(skuMapEl.textContent) || {})[vid] || ''; } catch (_) {}
      }
      if (vsku) csvQuick = window.RominaInventory.getQuickShip(vsku);
      if (csvQuick !== null) {
        // SKU is in the sheet — it's the source of truth.
        isQuick = csvQuick;
      } else if (window.RominaInventory.loaded && vsku) {
        // Sheet loaded but this exact SKU isn't in it. The metafield can't be
        // trusted for this specific SKU, so treat as standard (not quick-ship).
        isQuick = false;
      }
      // else: sheet not loaded yet -> keep the metafield value for now;
      // updateDelivery re-runs on 'romina:inventory:ready'.

      // Debug: surface what resolved for the current finish.
      host.setAttribute('data-romina-sku', vsku || '');
      host.setAttribute(
        'data-romina-csv',
        csvQuick !== null
          ? (csvQuick ? 'quick' : 'standard')
          : (window.RominaInventory.loaded ? 'not-in-sheet' : 'loading')
      );
    }

    const range = businessDatesRange(isQuick ? 10 : 50, isQuick ? 20 : 60);
    const out = host.querySelector('[data-romina-delivery-range]');
    if (out) out.textContent = range;
    // Append a short status label for Quick-Ship. Prefer the metafield text;
    // if the CSV drove the decision and there's no metafield text, show a
    // generic label so quick-ship still reads clearly.
    const statusOut = host.querySelector('[data-romina-delivery-status]');
    if (statusOut) {
      const label = rawStatus || (csvQuick === true ? 'Quick-Ship' : '');
      statusOut.textContent = isQuick && label ? ' · ' + label : '';
    }
  };

  const installHistoryHook = () => {
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function () {
      origPush.apply(this, arguments);
      updateDelivery();
    };
    history.replaceState = function () {
      origReplace.apply(this, arguments);
      updateDelivery();
    };
    window.addEventListener('popstate', updateDelivery);
  };

  // ---- upsell slider --------------------------------------------------------
  // Single-card carousel: each click advances by exactly one card width.
  document.addEventListener('click', (e) => {
    const prev = e.target.closest('[data-romina-upsell-prev]');
    const next = e.target.closest('[data-romina-upsell-next]');
    if (!prev && !next) return;
    e.preventDefault();
    const host = (prev || next).closest('[data-romina-upsell]');
    const track = host && host.querySelector('[data-romina-upsell-track]');
    if (!track) return;
    const card = track.querySelector('.romina-upsell__card');
    const cardWidth = card ? card.getBoundingClientRect().width : track.clientWidth;
    const max = track.scrollWidth - track.clientWidth;
    let target = track.scrollLeft + (next ? cardWidth : -cardWidth);
    // Loop: wrap to the other end when we reach the edge
    if (target > max + 1) target = 0;
    if (target < -1) target = max;
    track.scrollTo({ left: target, behavior: 'smooth' });
  });

  // ---- tabs accordion -------------------------------------------------------
  document.addEventListener('click', (e) => {
    const head = e.target.closest('.romina-tab__head');
    if (!head) return;
    e.preventDefault();
    const tab = head.closest('.romina-tab');
    const open = head.getAttribute('aria-expanded') === 'true';
    head.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (open) {
      tab.setAttribute('hidden-body', '');
    } else {
      tab.removeAttribute('hidden-body');
    }
  });

  // ---- multiAtc cart intercept ---------------------------------------------
  // When a product is flagged with atc.multi, the ATC submit has to become
  // multiple line items: the main variant + one line per selected add-on
  // (handles × N, feet × N, accent finish, custom option, fabric).
  //
  // We intercept the form submit, POST to /cart/add.js with an `items` array
  // instead of relying on the single-item default, then redirect to /cart (or
  // refresh the Pipeline drawer).
  const isCartDrawerTheme = () =>
    !!document.querySelector('cart-drawer, [data-cart-drawer]');

  const refreshCart = async () => {
    try {
      // Pipeline listens for this event to refresh its drawer/ mini-cart
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (_) { /* noop */ }
  };

  const interceptMultiAtc = (form) => {
    form.addEventListener(
      'submit',
      async (e) => {
        const multiField = form.querySelector('[name="multiAtc"]');
        const isMulti = multiField && multiField.value === 'true';
        if (!isMulti) return; // let Pipeline handle as usual

        const container = form.closest('[data-product-form-wrapper]') ||
          document.querySelector('[data-product-form-wrapper]');
        if (!container) return;

        // Gather base variant ID + quantity
        const idField = form.querySelector('[name="id"]');
        const qtyField = form.querySelector('[name="quantity"]');
        const variantId = idField && idField.value;
        const qty = Math.max(1, Number((qtyField && qtyField.value) || 1));
        if (!variantId) return;

        // Gather add-ons
        const items = [{ id: Number(variantId), quantity: qty }];
        container.querySelectorAll('[data-romina-addon-id]').forEach((input) => {
          const addonId = Number(input.value);
          if (!addonId) return;
          const addonQty = Number(input.getAttribute('data-qty')) || 1;
          items.push({ id: addonId, quantity: addonQty * qty });
        });

        e.preventDefault();
        try {
          const res = await fetch(
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root
              : '/') + 'cart/add.js',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/javascript',
              },
              body: JSON.stringify({ items }),
            }
          );
          if (!res.ok) throw new Error('cart/add failed');
          await refreshCart();
          if (!isCartDrawerTheme()) {
            window.location.href = '/cart';
          }
        } catch (err) {
          console.warn('Romina multiAtc failed, falling back to default', err);
          form.submit();
        }
      },
      { capture: true }
    );
  };

  // ---- main gallery video play overlay ------------------------------------
  const initProductVideoPlayOverlay = () => {
    const wrapper = document.querySelector('.product__media__wrapper');
    if (!wrapper) return;

    wrapper.querySelectorAll('.romina-media-play-overlay').forEach((btn) => {
      if (btn.dataset.rominaPlayInit) return;
      btn.dataset.rominaPlayInit = '1';

      const media = btn.closest('.product__media');
      if (!media) return;

      const syncVisibility = () => {
        const playing =
          !!media.querySelector('.plyr--playing') ||
          media.classList.contains('is-media-playing');
        btn.hidden = playing;
      };

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const plyrPlay = media.querySelector('.plyr__control--overlaid');
        if (plyrPlay) {
          plyrPlay.click();
        } else {
          const player = media.matches('[data-player]')
            ? media
            : media.querySelector('[data-player]');
          player?.dispatchEvent(new CustomEvent('play'));
        }

        setTimeout(syncVisibility, 80);
      });

      const plyrRoot = media.querySelector('.plyr');
      if (plyrRoot) {
        new MutationObserver(syncVisibility).observe(plyrRoot, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      media.addEventListener('play', syncVisibility);
      media.addEventListener('pause', syncVisibility);
      syncVisibility();
    });
  };

  // ---- mobile gallery video autoplay --------------------------------------
  const initProductVideoMobileAutoplay = () => {
    const slideshow = document.querySelector('[data-product-slideshow]');
    if (!slideshow || slideshow.dataset.rominaMobileVideoAutoplay) return;
    slideshow.dataset.rominaMobileVideoAutoplay = '1';

    const isMobileGallery = () =>
      window.matchMedia('(max-width: 767px)').matches;

    const isVideoSlide = (el) => {
      if (!el) return false;
      const type = el.getAttribute('data-type');
      return type === 'video' || type === 'external_video';
    };

    const playSlideVideo = (slide) => {
      if (!slide || !isVideoSlide(slide)) return;

      slideshow
        .querySelectorAll('.product__media[data-type="video"], .product__media[data-type="external_video"]')
        .forEach((el) => {
          if (el !== slide) el.dispatchEvent(new CustomEvent('pause'));
        });

      slide.dispatchEvent(new CustomEvent('play'));

      const video = slide.querySelector('video');
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.play().catch(() => {});
      }
    };

    let playTimer;
    const schedulePlay = () => {
      clearTimeout(playTimer);
      playTimer = setTimeout(() => {
        if (!isMobileGallery()) return;
        const slide = slideshow.querySelector('.product__media.is-selected');
        playSlideVideo(slide);
      }, 120);
    };

    slideshow.addEventListener('theme:image:change', schedulePlay);

    const observer = new MutationObserver(() => {
      if (!isMobileGallery()) return;
      const slide = slideshow.querySelector('.product__media.is-selected');
      if (!isVideoSlide(slide)) return;
      schedulePlay();
    });

    observer.observe(slideshow, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    setTimeout(schedulePlay, 700);
    setTimeout(schedulePlay, 1500);
  };

  // ---- wallpaper surface calculator ----------------------------------------
  const M2_PER_FT2 = 10.7639104167;
  const WALLPAPER_QTY_SCALE = 100;

  const formatSurfaceValue = (val) => String(parseFloat(Number(val).toFixed(2)));

  const initWallpaperCalculator = () => {
    const calc = document.querySelector('[data-romina-wallpaper-calc]');
    if (!calc || calc.dataset.rominaWallpaperInit) return;
    calc.dataset.rominaWallpaperInit = '1';

    const formWrap =
      calc.closest('[data-product-form-wrapper]') ||
      document.querySelector('[data-product-form-wrapper]');
    const form = formWrap && formWrap.querySelector('form[data-product-form]');
    const surfaceInput = calc.querySelector('[data-wallpaper-surface]');
    const qtyInput = calc.querySelector('[data-wallpaper-qty]');
    const unitPriceEl = calc.querySelector('[data-wallpaper-unit-price]');
    const totalEl = calc.querySelector('[data-wallpaper-total]');
    const errorEl = calc.querySelector('[data-wallpaper-error]');
    const propSurface = calc.querySelector('[data-wallpaper-prop-surface]');
    const propUnit = calc.querySelector('[data-wallpaper-prop-unit]');
    const unitSuffix = calc.querySelector('[data-wallpaper-unit-suffix]');
    const unitBtns = calc.querySelectorAll('[data-wallpaper-unit]');
    const atcBtn = formWrap && formWrap.querySelector('[data-add-to-cart]');

    const minM2 = Number(calc.getAttribute('data-min-m2')) || 2;
    const minFt2 =
      Number(calc.getAttribute('data-min-ft2')) || minM2 * M2_PER_FT2;
    let activeUnit = calc.getAttribute('data-default-unit') || 'ft2';

    const unitLabel = (unit) => (unit === 'ft2' ? 'ft²' : 'm²');

    const surfaceToFt2 = (val, unit) => {
      const n = Number(val);
      if (!isFinite(n) || n <= 0) return 0;
      return unit === 'ft2' ? n : n * M2_PER_FT2;
    };

    const convertSurface = (val, fromUnit, toUnit) => {
      const n = Number(val);
      if (!isFinite(n)) return '';
      if (fromUnit === toUnit) return formatSurfaceValue(n);
      if (fromUnit === 'm2' && toUnit === 'ft2') return formatSurfaceValue(n * M2_PER_FT2);
      return formatSurfaceValue(n / M2_PER_FT2);
    };

    const setUnit = (unit) => {
      if (unit === activeUnit) return;
      const current = surfaceInput && surfaceInput.value;
      const prevUnit = activeUnit;
      activeUnit = unit;
      unitBtns.forEach((btn) => {
        const isActive = btn.getAttribute('data-wallpaper-unit') === unit;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (unitSuffix) unitSuffix.textContent = unitLabel(unit);
      if (surfaceInput && current) {
        surfaceInput.value = convertSurface(current, prevUnit, unit);
      }
      recalc();
    };

    const recalc = (resolvedIn) => {
      const resolved =
        resolvedIn ||
        resolveCombinedVariant(formWrap) ||
        (() => {
          const jsonEl = document.querySelector('[data-romina-product-json]');
          if (!jsonEl) return null;
          try {
            const data = JSON.parse(jsonEl.textContent);
            const v = (data.variants || []).find((x) => x.available) || (data.variants || [])[0];
            return v
              ? { price: v.price, compareAt: v.compare_at_price, id: v.id }
              : null;
          } catch (_) {
            return null;
          }
        })();

      if (!resolved || resolved.price == null) return;

      const rawSurface = surfaceInput ? Number(surfaceInput.value) : 0;
      const surfaceFt2 = surfaceToFt2(rawSurface, activeUnit);
      const unitSymbol = unitLabel(activeUnit);
      const pricePerFt2 = resolved.price * WALLPAPER_QTY_SCALE;
      const pricePerM2 = pricePerFt2 * M2_PER_FT2;
      const displayUnitPrice = activeUnit === 'ft2' ? pricePerFt2 : pricePerM2;
      const cartQty = Math.max(1, Math.round(surfaceFt2 * WALLPAPER_QTY_SCALE));
      const totalCents = resolved.price * cartQty;
      const comparePerFt2 =
        resolved.compareAt && resolved.compareAt > resolved.price
          ? resolved.compareAt * WALLPAPER_QTY_SCALE
          : 0;
      const comparePerM2 = comparePerFt2 > 0 ? comparePerFt2 * M2_PER_FT2 : 0;
      const compareTotal =
        comparePerFt2 > 0 ? resolved.compareAt * cartQty : 0;

      let valid = isFinite(rawSurface) && rawSurface > 0 && surfaceFt2 >= minFt2;
      if (errorEl) {
        if (!isFinite(rawSurface) || rawSurface <= 0) {
          errorEl.textContent = 'Enter a surface area greater than zero.';
          errorEl.hidden = false;
        } else if (surfaceFt2 < minFt2) {
          errorEl.textContent =
            'Minimum order is ' +
            formatSurfaceValue(minFt2) +
            ' ft² (' +
            formatSurfaceValue(minM2) +
            ' m²).';
          errorEl.hidden = false;
        } else {
          errorEl.hidden = true;
        }
      }

      if (unitPriceEl) {
        unitPriceEl.textContent = fmtMoney(displayUnitPrice) + ' / ' + unitSymbol;
      }
      if (totalEl) {
        totalEl.textContent = fmtMoney(totalCents);
      }

      document.querySelectorAll('[data-product-price]').forEach((el) => {
        el.textContent = fmtMoney(displayUnitPrice) + ' / ' + unitSymbol;
      });
      document.querySelectorAll('[data-button-price]').forEach((el) => {
        el.textContent = fmtMoney(totalCents);
      });

      const onSale = compareTotal > totalCents;
      const displayCompareUnitPrice =
        activeUnit === 'ft2' ? comparePerFt2 : comparePerM2;
      document.querySelectorAll('[data-compare-price]').forEach((el) => {
        if (onSale) {
          el.textContent = fmtMoney(displayCompareUnitPrice) + ' / ' + unitSymbol;
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });

      const picker = document.querySelector('[data-romina-picker]');
      if (picker) picker.setAttribute('data-base-price', resolved.price);

      const qtyEl =
        (formWrap && formWrap.querySelector('[name="quantity"]')) ||
        qtyInput;
      if (qtyEl && valid) {
        qtyEl.value = String(cartQty);
        qtyEl.dispatchEvent(new Event('change', { bubbles: true }));
      }

      try {
        const productFormEl =
          (formWrap && formWrap.querySelector('product-form')) ||
          document.querySelector('product-form[data-form-holder]');
        const alpineData =
          productFormEl && window.Alpine && window.Alpine.$data
            ? window.Alpine.$data(productFormEl)
            : null;
        if (alpineData && typeof alpineData.quantity !== 'undefined') {
          alpineData.quantity = valid ? cartQty : 1;
        }
      } catch (_) { /* noop */ }

      if (propSurface && isFinite(rawSurface) && rawSurface > 0) {
        propSurface.value = formatSurfaceValue(rawSurface) + ' ' + unitSymbol;
      }
      if (propUnit) propUnit.value = unitSymbol;

      if (atcBtn) {
        atcBtn.disabled = !valid;
        atcBtn.setAttribute('aria-disabled', valid ? 'false' : 'true');
      }
    };

    calc.__rominaRecalc = recalc;

    unitBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        setUnit(btn.getAttribute('data-wallpaper-unit') || 'ft2');
      });
    });

    if (surfaceInput) {
      surfaceInput.addEventListener('input', () => recalc());
      surfaceInput.addEventListener('change', () => recalc());
    }

    if (form) {
      form.addEventListener(
        'submit',
        (e) => {
          const surfaceFt2 = surfaceToFt2(surfaceInput && surfaceInput.value, activeUnit);
          if (!isFinite(surfaceFt2) || surfaceFt2 <= 0 || surfaceFt2 < minFt2) {
            e.preventDefault();
            e.stopPropagation();
            recalc();
            if (surfaceInput) surfaceInput.focus();
          } else {
            recalc();
          }
        },
        { capture: true }
      );
    }

    recalc();
  };

  // ---- init -----------------------------------------------------------------
  const init = () => {
    // Hide native Pipeline variant picker on pages where our picker is present
    const hasPicker = document.querySelector('[data-romina-picker]');
    if (hasPicker) {
      const wrap = hasPicker.closest('[data-product-form-wrapper]');
      if (wrap) wrap.classList.add('romina-picker-active');
    }

    updateDelivery();
    installHistoryHook();

    document
      .querySelectorAll('form[data-product-form]')
      .forEach((f) => {
        if (f.dataset.rominaIntercepted) return;
        f.dataset.rominaIntercepted = '1';
        interceptMultiAtc(f);
      });

    document
      .querySelectorAll('[data-product-form-wrapper]')
      .forEach(recalcPrice);

    // Sync the displayed price to the initially-selected variant (covers
    // ?variant= deep links and price-bearing default options like Handles).
    if (hasPicker) {
      const initWrap =
        hasPicker.closest('[data-product-form-wrapper]') ||
        document.querySelector('[data-product-form-wrapper]');
      if (initWrap) updateVariantPrice(resolveCombinedVariant(initWrap));
    }

    initProductVideoPlayOverlay();
    initProductVideoMobileAutoplay();
    initWallpaperCalculator();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Pipeline replaces the product section on variant change; re-init then.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('section-render', init);

  // Quantity change makes Alpine re-evaluate the button's x-text from the baked
  // default-variant price, reverting our patch. Re-drive the price off the live
  // resolved variant after the qty input settles.
  const onQtyChange = (e) => {
    const qtyInput = e.target.closest && e.target.closest('[name="quantity"]');
    if (!qtyInput) return;
    const calc = document.querySelector('[data-romina-wallpaper-calc]');
    if (calc && qtyInput.hasAttribute('data-wallpaper-qty')) return;
    const wrap =
      qtyInput.closest('[data-product-form-wrapper]') ||
      document.querySelector('[data-product-form-wrapper]');
    setTimeout(() => {
      if (calc && typeof calc.__rominaRecalc === 'function') {
        calc.__rominaRecalc();
        return;
      }
      updateVariantPrice(resolveCombinedVariant(wrap));
    }, 0);
  };
  document.addEventListener('change', onQtyChange);
  document.addEventListener('input', onQtyChange);

  // #variant photos are not gallery slides. Show the matching overlay, or
  // hide it when the chosen variant uses a normal gallery image.
  const syncVariantStage = (event) => {
    const detail = event.detail || {};
    const id = detail.id == null ? '' : String(detail.id);
    const origin = event.target instanceof Element ? event.target : null;
    const frame = origin && origin.closest ? origin.closest('.romina-slideshow-frame') : null;
    const stages = frame
      ? [frame.querySelector('[data-variant-stage]')].filter(Boolean)
      : Array.from(document.querySelectorAll('[data-variant-stage]'));
    stages.forEach((stage) => {
      if (detail.found) {
        stage.hidden = true;
        return;
      }
      let matched = false;
      stage.querySelectorAll('[data-variant-media-id]').forEach((slide) => {
        const on = slide.getAttribute('data-variant-media-id') === id;
        slide.hidden = !on;
        if (on) matched = true;
      });
      stage.hidden = !matched;
    });
  };
  document.addEventListener('romina:variant-media', syncVariantStage);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-variant-zoom]');
    if (!button) return;
    event.preventDefault();
    const slide = button.closest('[data-variant-media-id]');
    const root = document.querySelector('[data-zoom-wrapper]');
    const lib = window.themePhotoswipe;
    const PhotoSwipe = lib && lib.PhotoSwipe && lib.PhotoSwipe.default;
    const PhotoSwipeUI = lib && lib.PhotoSwipeUI && lib.PhotoSwipeUI.default;
    if (!slide || !root || !PhotoSwipe || !PhotoSwipeUI) return;
    const item = {
      src: slide.getAttribute('data-zoom-src'),
      w: Number(slide.getAttribute('data-zoom-w')) || 1600,
      h: Number(slide.getAttribute('data-zoom-h')) || 1600
    };
    const gallery = new PhotoSwipe(root, PhotoSwipeUI, [item], {
      index: 0,
      history: false,
      bgOpacity: 1,
      modal: false
    });
    gallery.init();
  });

  // Re-run the ETA once the live CSV inventory finishes loading (bound once,
  // module scope — not inside init — so it isn't duplicated on re-init).
  document.addEventListener('romina:inventory:ready', updateDelivery);

  // Quickview and other AJAX-injected product forms: watch the DOM for
  // new [data-product-form-wrapper] nodes and rerun init() when they appear.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (
          node.matches && (
            node.matches('[data-product-form-wrapper]') ||
            node.querySelector('[data-product-form-wrapper]')
          )
        ) {
          init();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();